import { Button } from "common/buttons";
import { MultilineText } from "common/components/MultilineText";
import { ResponsiveComponent } from "common/components/ResponsiveComponent";
import { H2 } from "common/components/Typography";
import { useToggle } from "common/hooks/useToggle";
import gql from "graphql-tag";
import React, { useState } from "react";
import {
  ContentBreadcrumbFragment_context,
  MultipleChoiceData,
} from "__generated__/types";
import styles from "./MultipleChoice.module.css";
import { MultipleChoiceQuestion } from "./MultipleChoiceQuestion";
import { MultipleChoiceSummary } from "./MultipleChoiceSummary";
import { Result } from "./Result";

export const multipleChoiceFragment = gql`
  fragment MultipleChoiceData on MultipleChoice {
    id
    type
    title
    context {
      id
      title
    }
    ingress {
      content
      isMarkdown
    }
    relevantContent {
      content
      isMarkdown
    }
    latestSubmitted {
      scoreInPercent
      submittedAt
    }
    options {
      id
      title
      correct
    }
    questions {
      id
      title
      body {
        content
        isMarkdown
      }
      options {
        id
        title
        correct
      }
    }
    canUpdateDetails {
      canPerform
    }
    canCreateProgression {
      canPerform
    }
  }
`;

export interface QuestionState {
  selectedOptions: string[];
}

/**
 * The key in the record is the question ID.
 */
export type FormQuestions = Record<string, QuestionState | undefined>;

interface Props {
  multipleChoice: MultipleChoiceData;
  onFinishedElement?: () => void;
  isSyllabus?: boolean;
  containerType:
    | ContentBreadcrumbFragment_context["__typename"]
    | "SelfAssessment"
    | undefined;
}

export const MultipleChoice: React.FC<React.PropsWithChildren<Props>> = ({
  multipleChoice,
  onFinishedElement,
  isSyllabus,
  containerType,
}) => {
  const [currentQuestionIdx, setCurrentQuestionIdx] = useState<number>(0);
  const [showPreviousScore, setShowPreviousScore] = useToggle(
    !!multipleChoice.latestSubmitted,
  );
  const [showSummary, setShowSummary] = useState(false);
  const [formQuestions, setFormQuestions] = useState<FormQuestions>({});
  const previousScore = multipleChoice.latestSubmitted?.scoreInPercent;

  function setFormQuestionValue(questionId: string, value: QuestionState) {
    setFormQuestions({
      ...formQuestions,
      [questionId]: value,
    });
  }

  function handleResetForm(): void {
    setShowSummary(false);
    setCurrentQuestionIdx(0);
  }

  function handleClickNext(): void {
    if (currentQuestionIdx === multipleChoice.questions.length - 1) {
      setShowSummary(true);
    } else {
      setCurrentQuestionIdx(currentQuestionIdx + 1);
    }
  }

  const currentQuestion =
    multipleChoice.questions.length > 0
      ? multipleChoice.questions[currentQuestionIdx]
      : undefined;

  //TODO: Check if plaintTextIngress is supposed to be here, or if it should look like sketches
  return (
    <div className={styles.container}>
      <H2>{multipleChoice.title}</H2>
      {multipleChoice.ingress && (
        <MultilineText {...multipleChoice.ingress} openLinksSeparately />
      )}

      {showPreviousScore &&
      typeof previousScore === "number" &&
      containerType !== "SelfAssessment" ? (
        <Result
          showNextElementButton={false}
          score={previousScore}
          summaryText="Sist gang fikk du"
          resetForm={() => {
            setShowPreviousScore();
            handleResetForm();
          }}
        />
      ) : !currentQuestion ? (
        <p>Oisann! Denne oppgaven har ikke fått noen spørsmål enda.</p>
      ) : showSummary ? (
        <MultipleChoiceSummary
          multipleChoiceId={multipleChoice.id}
          formQuestions={formQuestions}
          questions={multipleChoice.questions}
          goToQuestion={(idx) => {
            setShowSummary(false);
            setCurrentQuestionIdx(idx);
          }}
          resetForm={handleResetForm}
          onFinishedElement={onFinishedElement}
          isSyllabus={isSyllabus}
          containerType={containerType}
        />
      ) : (
        <div className={styles.content}>
          <p className={styles.activeQuestionNumber}>
            Spørsmål {currentQuestionIdx + 1} av{" "}
            {multipleChoice.questions.length}
          </p>

          <MultipleChoiceQuestion
            question={currentQuestion}
            value={formQuestions[currentQuestion.id] ?? { selectedOptions: [] }}
            onChange={(value: QuestionState) => {
              setFormQuestionValue(currentQuestion.id, value);
            }}
          />
          <div className={styles.buttonRow}>
            <Button
              onClick={() => {
                setCurrentQuestionIdx(currentQuestionIdx - 1);
              }}
              kind={currentQuestionIdx > 0 ? undefined : "disabled"}
            >
              Tilbake
            </Button>
            <Button onClick={handleClickNext}>
              <ResponsiveComponent
                belowThreshold="Neste spm."
                aboveThreshold="Neste spørsmål"
              />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
